import { CanalNotificacao } from '../../enum/parametros/canal-notificacao.enum';
import {
  CondicaoElegibilidadeResponseDto,
  ExtratoDesligamentoResponseDto,
  ExtratoInstitutoResponseDto,
  ExtratoNotificacoesResponseDto,
  ExtratoRegraResponseDto,
  SalvarExtratoDesligamentoCommandDto,
  SalvarExtratoNotificacoesCommandDto,
  SituacaoCadastralResponseDto,
} from '../../models/parametros/extrato-desligamento-api.model';
import {
  AlteracaoExtrato,
  CalendarizacaoNotificacoes,
  ExtratoDesligamento,
  ExtratoDesligamentoConfiguracao,
  InstitutoExtrato,
  OpcaoCondicao,
  OpcaoSituacao,
  RegraElegibilidade,
} from '../../models/parametros/extrato-desligamento.model';
import { CANAIS_NOTIFICACAO_OPCOES, parametroCondicao } from '../../utils/parametros/extrato-desligamento.defaults';
import { tipoInstitutoDoNome } from './instituto-param-api.mapper';

/** Usuário exibido quando a última alteração foi feita por carga/sistema (sem operador). */
export const USUARIO_SISTEMA = 'Sistema';

/** Flag da API de cada canal de notificação. */
type FlagCanal = 'fl_canal_email' | 'fl_canal_portal_partic' | 'fl_canal_sms';

const FLAG_DO_CANAL: Record<CanalNotificacao, FlagCanal> = {
  [CanalNotificacao.Email]: 'fl_canal_email',
  [CanalNotificacao.PortalParticipante]: 'fl_canal_portal_partic',
  [CanalNotificacao.Sms]: 'fl_canal_sms',
};

export function situacaoDtoToOpcao(dto: Pick<SituacaoCadastralResponseDto, 'id' | 'nome' | 'nome_exibicao'>): OpcaoSituacao {
  return { value: dto.id, label: (dto.nome_exibicao || dto.nome).trim() };
}

export function condicaoDtoToOpcao(dto: CondicaoElegibilidadeResponseDto): OpcaoCondicao {
  const label = (dto.nome_exibicao || dto.nome).trim();
  return {
    value: dto.id,
    label,
    ...(dto.fl_parametrizavel ? { parametro: parametroCondicao(dto.nome, label, dto.qtd_minimo, dto.qtd_maximo) } : {}),
    ...(dto.fl_status ? {} : { inativa: true }),
  };
}

/**
 * Monta o que a tela carrega. Os catálogos vêm só com os registros ativos; uma situação ou condição
 * já gravada que saiu do catálogo entra no fim da lista (a condição, como inativa), para continuar
 * com nome na tela.
 */
export function extratoDtoToExtratoDesligamento(
  dto: ExtratoDesligamentoResponseDto,
  situacoesCatalogo: readonly SituacaoCadastralResponseDto[],
  condicoesCatalogo: readonly CondicaoElegibilidadeResponseDto[],
): ExtratoDesligamento {
  const situacoes = situacoesCatalogo.map(situacaoDtoToOpcao);
  for (const situacao of (dto.institutos ?? []).flatMap((instituto) => instituto.situacoes_cadastrais ?? [])) {
    if (!situacoes.some((opcao) => opcao.value === situacao.id)) {
      situacoes.push(situacaoDtoToOpcao(situacao));
    }
  }

  const condicoes = condicoesCatalogo.map(condicaoDtoToOpcao);
  for (const regra of (dto.institutos ?? []).flatMap((instituto) => instituto.regras ?? [])) {
    for (const item of regra.condicoes ?? []) {
      if (item.condicao && !condicoes.some((opcao) => opcao.value === item.id_condicao_elegib)) {
        condicoes.push(
          condicaoDtoToOpcao({ ...item.condicao, id: item.id_condicao_elegib, fl_status: false }),
        );
      }
    }
  }

  return {
    configuracao: extratoDtoToConfiguracao(dto, situacoes),
    situacoes,
    condicoes,
    prazoOpcaoDias: dto.nr_dias_prazo_opc > 0 ? dto.nr_dias_prazo_opc : null,
    ultimaAlteracao: extratoDtoToUltimaAlteracao(dto),
  };
}

/** Situações de cada instituto na ordem das opções da tela. */
export function extratoDtoToConfiguracao(
  dto: ExtratoDesligamentoResponseDto,
  situacoes: readonly OpcaoSituacao[],
): ExtratoDesligamentoConfiguracao {
  return {
    institutos: (dto.institutos ?? []).map((instituto) => institutoDtoToInstitutoExtrato(instituto, situacoes)),
    adesaoAutomatica: {
      habilitada: dto.fl_adesao_automatica,
      institutoPadrao: dto.id_inst_param_adesao ?? null,
    },
  };
}

export function extratoDtoToUltimaAlteracao(dto: ExtratoDesligamentoResponseDto): AlteracaoExtrato | null {
  return dto.id == null ? null : ultimaAlteracao(dto);
}

/** Canais na ordem da tela. */
export function notificacoesDtoToCalendarizacao(dto: ExtratoNotificacoesResponseDto): CalendarizacaoNotificacoes {
  return {
    ativa: dto.fl_notif_automatica,
    antecedenciasDias: [...(dto.antecedencias_dias ?? [])],
    canais: CANAIS_NOTIFICACAO_OPCOES.map((opcao) => opcao.value).filter((canal) => dto[FLAG_DO_CANAL[canal]]),
  };
}

/** Plano ainda sem extrato (sem `dt_ult_atu`) não tem última alteração. */
export function notificacoesDtoToUltimaAlteracao(dto: ExtratoNotificacoesResponseDto): AlteracaoExtrato | null {
  return ultimaAlteracao(dto);
}

/** Envia as escolhas como estão, inclusive com as notificações desligadas. */
export function calendarizacaoToSalvarCommand(notificacoes: CalendarizacaoNotificacoes): SalvarExtratoNotificacoesCommandDto {
  const marcado = (canal: CanalNotificacao) => notificacoes.canais.includes(canal);
  return {
    fl_notif_automatica: notificacoes.ativa,
    antecedencias_dias: [...notificacoes.antecedenciasDias],
    fl_canal_email: marcado(CanalNotificacao.Email),
    fl_canal_portal_partic: marcado(CanalNotificacao.PortalParticipante),
    fl_canal_sms: marcado(CanalNotificacao.Sms),
  };
}

function ultimaAlteracao(
  dto: Pick<ExtratoDesligamentoResponseDto, 'dt_ult_atu' | 'nome_operador_ult_atu'>,
): AlteracaoExtrato | null {
  if (!dto.dt_ult_atu) {
    return null;
  }
  return { usuario: dto.nome_operador_ult_atu?.trim() || USUARIO_SISTEMA, dataHora: dto.dt_ult_atu };
}

/** Instituto ainda não parametrizado no extrato (`fl_habilitado` nulo) aparece habilitado. */
function institutoDtoToInstitutoExtrato(dto: ExtratoInstitutoResponseDto, situacoes: readonly OpcaoSituacao[]): InstitutoExtrato {
  const gravadas = new Set((dto.situacoes_cadastrais ?? []).map((situacao) => situacao.id));
  return {
    idInstParam: dto.id_inst_param,
    tipo: tipoInstitutoDoNome(dto.tipo_instituto?.nome),
    nomeExibicao: (dto.tipo_instituto?.nome_exibicao ?? dto.tipo_instituto?.nome ?? '').trim(),
    habilitado: dto.fl_habilitado ?? true,
    situacoesCadastrais: situacoes.map((opcao) => opcao.value).filter((id) => gravadas.has(id)),
    regras: (dto.regras ?? []).map(regraDtoToRegra),
  };
}

function regraDtoToRegra(dto: ExtratoRegraResponseDto): RegraElegibilidade {
  return {
    id: String(dto.id),
    idApi: dto.id,
    condicoes: (dto.condicoes ?? []).map((item) =>
      item.qtd_parametro != null
        ? { idCondicao: item.id_condicao_elegib, valor: item.qtd_parametro }
        : { idCondicao: item.id_condicao_elegib },
    ),
    mensagem: dto.desc_msg_nao_elegivel ?? '',
  };
}

/** Envia todos os institutos da tela; com a adesão desativada, o instituto padrão vai nulo. */
export function configuracaoToSalvarCommand(configuracao: ExtratoDesligamentoConfiguracao): SalvarExtratoDesligamentoCommandDto {
  const { habilitada, institutoPadrao } = configuracao.adesaoAutomatica;
  return {
    institutos: configuracao.institutos.map((instituto) => ({
      id_inst_param: instituto.idInstParam,
      fl_habilitado: instituto.habilitado,
      id_situacoes: [...instituto.situacoesCadastrais],
      regras: instituto.regras.map((regra) => ({
        id: regra.idApi,
        desc_msg_nao_elegivel: regra.mensagem.trim() || null,
        condicoes: regra.condicoes.map((condicao) => ({
          id_condicao_elegib: condicao.idCondicao,
          qtd_parametro: condicao.valor ?? null,
        })),
      })),
    })),
    fl_adesao_automatica: habilitada,
    id_inst_param_adesao: habilitada ? institutoPadrao : null,
  };
}
